import { axiosChartInstance } from "config";
import { useAxiosMutation } from "hooks";
import { useQueryClient } from "react-query";

type UseDeleteAuditCommentsProps = {
  encounterId: string;
  chartCode: string;
  sectionCode: string;
  subsectionCode?: string;
};

function useDeleteAuditComments({
  encounterId,
  chartCode,
  sectionCode,
  subsectionCode,
}: UseDeleteAuditCommentsProps) {
  const queryClient = useQueryClient();

  return useAxiosMutation(
    [
      "deleteAuditComments",
      { encounterId, chartCode, sectionCode, subsectionCode },
    ],
    (commentId: string) => {
      const endpoint = subsectionCode
        ? `/v1/audits/subsection/comments/${encounterId}/${chartCode}/${sectionCode}/${subsectionCode}/${commentId}`
        : `/v1/audits/section/comments/${encounterId}/${chartCode}/${sectionCode}/${commentId}`;
      return axiosChartInstance.delete(endpoint);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "AuditComments",
          { encounterId, chartCode, sectionCode, subsectionCode },
        ]);
        queryClient.invalidateQueries([
          "AuditCommentsSectionIndicator",
          { encounterId, chartCode, sectionCode, validateSubsections: true },
        ]);
        queryClient.invalidateQueries([
          "AuditCommentsSectionIndicator",
          { encounterId, chartCode, sectionCode, validateSubsections: false },
        ]);
        if (subsectionCode) {
          queryClient.invalidateQueries([
            "AuditCommentsSubsectionIndicator",
            { encounterId, chartCode, sectionCode, subsectionCode },
          ]);
        }
      },
    }
  );
}

type UseDeleteAuditCommentsSharedProps = {
  encounterId: string;
  sectionCode: string;
  subsectionCode?: string;
};

function useDeleteAuditCommentsShared({
  encounterId,
  sectionCode,
  subsectionCode,
}: UseDeleteAuditCommentsSharedProps) {
  const queryClient = useQueryClient();

  return useAxiosMutation(
    ["deleteSharedAuditComments", { encounterId, sectionCode, subsectionCode }],
    (commentId: string) => {
      const endpoint = subsectionCode
        ? `/v1/audits/subsection/comments/${encounterId}/shared/${sectionCode}/${subsectionCode}/${commentId}`
        : `/v1/audits/section/comments/${encounterId}/shared/${sectionCode}/${commentId}`;
      return axiosChartInstance.delete(endpoint);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          "AuditCommentsShared",
          { encounterId, sectionCode, subsectionCode },
        ]);
        queryClient.invalidateQueries([
          "AuditCommentsSharedSectionIndicator",
          { encounterId, sectionCode },
        ]);
        if (subsectionCode) {
          queryClient.invalidateQueries([
            "AuditCommentsSharedSubsectionIndicator",
            { encounterId, sectionCode, subsectionCode },
          ]);
        }
      },
    }
  );
}

export { useDeleteAuditComments, useDeleteAuditCommentsShared };
